"use client"

import { useState } from "react"
import { X } from "lucide-react"
import UploadStep from "./steps/UploadStep"
import SchemaStep from "./steps/SchemaStep"
import PreviewStep from "./steps/PreviewStep"
import ProgressStep from "./steps/ProgressStep"
import type { ImportRow } from "../types"

interface ContactImportWizardProps {
  onClose: () => void
  onSuccess: () => void
}

export default function ContactImportWizard({ onClose, onSuccess }: ContactImportWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [error, setError] = useState("")

  // State for each step
  const [excelFile, setExcelFile] = useState<File | null>(null)
  const [sampleHeaders, setSampleHeaders] = useState<string[]>([])
  const [sampleRows, setSampleRows] = useState<string[][]>([])
  const [finalSchema, setFinalSchema] = useState<string[]>([])
  const [exampleRows, setExampleRows] = useState<Record<string, string>[]>([])
  const [importedRecords, setImportedRecords] = useState<ImportRow[]>([])
  const [jobId, setJobId] = useState<string | null>(null)

  const handleUploadSuccess = (file: File, headers: string[], rows: string[][]) => {
    setExcelFile(file)
    setSampleHeaders(headers)
    setSampleRows(rows)
    setCurrentStep(2)
  }

  const handleSchemaSuccess = (schema: string[], examples: Record<string, string>[]) => {
    setFinalSchema(schema)
    setExampleRows(examples)
    setCurrentStep(3)
  }

  const handlePreviewSuccess = (records: ImportRow[], jobId: string | null) => {
    if (jobId) {
      // queued import: proceed to progress step
      setImportedRecords(records)
      setJobId(jobId)
      setCurrentStep(4)
    } else {
      // synchronous import: close wizard and refresh list
      onSuccess()
    }
  }

  const handleComplete = () => {
    onSuccess()
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Import Contacts</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-800"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`flex-1 relative ${
                step < 4
                  ? "after:content-[''] after:h-[2px] after:w-full after:absolute after:top-1/2 after:-right-1/2 after:z-0"
                  : ""
              } ${step <= currentStep ? "after:bg-blue-500" : "after:bg-gray-700"}`}
            >
              <div
                className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                  step < currentStep
                    ? "bg-blue-500 text-white"
                    : step === currentStep
                      ? "bg-blue-600 text-white ring-4 ring-blue-900"
                      : "bg-gray-700 text-gray-400"
                }`}
              >
                {step}
              </div>
              <div className={`mt-2 text-xs text-center ${step <= currentStep ? "text-gray-300" : "text-gray-500"}`}>
                {step === 1 ? "Upload" : step === 2 ? "Map Fields" : step === 3 ? "Preview" : "Complete"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && <div className="mb-4 bg-red-900/30 border border-red-700 p-3 rounded-lg text-sm">{error}</div>}

      {/* Step Content */}
      <div className="bg-gray-800 p-4 rounded-lg">
        {currentStep === 1 && <UploadStep onSuccess={handleUploadSuccess} onError={setError} />}
        {currentStep === 2 && excelFile && (
          <SchemaStep
            excelFile={excelFile}
            sampleHeaders={sampleHeaders}
            sampleRows={sampleRows}
            onSuccess={handleSchemaSuccess}
            onBack={() => setCurrentStep(1)}
            onError={setError}
          />
        )}
        {currentStep === 3 && excelFile && (
          <PreviewStep
            excelFile={excelFile}
            finalSchema={finalSchema}
            sampleHeaders={sampleHeaders}
            sampleRows={sampleRows}
            exampleRows={exampleRows}
            onSuccess={handlePreviewSuccess}
            onBack={() => setCurrentStep(2)}
            onError={setError}
          />
        )}
        {currentStep === 4 && (
          <ProgressStep
            importedRecords={importedRecords}
            jobId={jobId}
            onComplete={handleComplete}
            onError={setError}
          />
        )}
      </div>
    </div>
  )
}
